import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import useRedirectToInformativeDealUrl from '@/hooks/use-redirect-to-informative-deal-url';
import useUsers from '@/hooks/use-users';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { VehicleListing } from '@/models/registry';
import { dealStore } from '@/models/stores';
import type { Customer } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { compact, noop, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { transformToSalesRepOptions } from './sales-reps-selectors';

type Variables = {
  salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
  position: number;
};

export type SalesRepComboboxProps = {
  dealShortId: Deal['short_id'];
  dealId: Deal['id'];
  whichSalesRep: 'primary' | 'secondary';
  afterOnChange?: () => void;
  primaryCustomerFullName: Customer['full_name'];
  vehicle: VehicleListing;
  primarySalesRep: Deal['primary_sales_rep'];
  secondarySalesRep: Deal['secondary_sales_rep'];
  onSuccess?: (data: Deal, variables: Variables) => void;
  onError?: (error: Error, variables: Variables) => void;
};

export function SalesRepCombobox({
  dealShortId,
  dealId,
  whichSalesRep,
  afterOnChange,
  primaryCustomerFullName,
  vehicle,
  primarySalesRep,
  secondarySalesRep,
  onSuccess,
  onError,
}: SalesRepComboboxProps) {
  // const { deal } = useDeal(dealShortId);
  const deal = dealStore.getByShortId(dealShortId);
  const salesReps = compact([
    deal?.primary_sales_rep,
    deal?.secondary_sales_rep,
  ]);
  const salesRep1 = salesReps?.[0];
  const salesRep2 = salesReps?.[1];

  const { salesReps: allSalesReps } = useUsers();

  const queryClient = useQueryClient();

  const { redirectToInformativeDealUrl } = useRedirectToInformativeDealUrl();

  const updateSalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: salesReps.map((rep) => rep?.id),
      });
    },
    mutationFn: async ({
      salesReps,
      position,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
      position: number;
    }) => {
      const salesRepsIds = compact(salesReps).map((rep) => String(rep?.id));

      const route =
        position === 0
          ? `deals/${dealId}/primary-sales-rep/${salesRepsIds[0]}`
          : `deals/${dealId}/secondary-sales-rep/${salesRepsIds[1]}`;

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesRepsIds[position],
        })
        .catch((e) => {
          console.error('Error updating sales reps', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating sales reps', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const deletePrimarySalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: [null, salesReps[1]?.id],
      });
    },
    mutationFn: async ({
      salesReps,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
    }) => {
      const route = `deals/${dealId}/primary-sales-rep/${salesReps[0]?.id}/delete`;
      // /deals/:id/primary-sales-rep/:user_id/delete

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesReps[0]?.id,
        })
        .catch((e) => {
          console.error('Error deleting primary sales rep', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error deleting primary sales rep', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const deleteSecondarySalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: [salesReps[0]?.id, null],
      });
    },
    mutationFn: async ({
      salesReps,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
    }) => {
      const route = `deals/${dealId}/secondary-sales-rep/${salesReps[1]?.id}/delete`;
      // /deals/:id/secondary-sales-rep/:user_id/delete

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesReps[1]?.id,
        })
        .catch((e) => {
          console.error('Error deleting secondary sales rep', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      redirectToInformativeDealUrl({
        primaryCustomerFullName,
        vehicle,
        primarySalesRep,
        secondarySalesRep,
        shortId: dealShortId,
      });
    },
    onError: (error, variables) => {
      console.error('Error deleting secondary sales rep', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const salesRepOptions = useMemo(() => {
    return transformToSalesRepOptions(allSalesReps);
  }, [allSalesReps]);

  const handleSalesRepChange = useCallback(
    (position: number) => (newSalesRepId: string) => {
      if (newSalesRepId === 'no-sales-rep') {
        if (position === 0) {
          deletePrimarySalesRepsMutation.mutate({
            salesReps: [salesRep1, salesRep2],
          });
        } else {
          deleteSecondarySalesRepsMutation.mutate({
            salesReps: [salesRep1, salesRep2],
          });
        }
        return;
      }

      const newSalesReps = [...salesReps];

      const newSalesRep = allSalesReps.find(
        (option) => option.id === newSalesRepId,
      );

      if (!newSalesRep) return;

      newSalesReps[position] = newSalesRep;

      const newSalesRepsIdUnique = uniqBy(newSalesReps, 'id');
      updateSalesRepsMutation.mutate({
        salesReps: newSalesRepsIdUnique,
        position,
      });
    },
    [salesReps],
  );

  const onChange = useCallback(
    (newSalesRepId: string) => {
      handleSalesRepChange(
        whichSalesRep === 'primary' ? 0 : whichSalesRep === 'secondary' ? 1 : 0,
      )(newSalesRepId);

      afterOnChange?.();
    },
    [handleSalesRepChange, whichSalesRep],
  );

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_SALES_REP_1}</KeyboardKeyIcon>
      }
      placeholder="Set sales rep..."
      onChange={onChange}
      emptyMessage="No sales reps found"
      options={salesRepOptions}
      setOpen={noop}
      onKeyDown={(e) => {
        const keyPressed = e.key;
        const option = salesRepOptions.find(
          (option) => option.value === keyPressed,
        );
        if (option) {
          onChange(option.value);
        }
      }}
    ></Combobox>
  );
}
